// v0.4.6 sync-kil client.
//
// The KIL view in the SPA fires triggerSyncKil() on every entry — link
// click, URL nav, back/forward, refresh. The Worker checks for pending
// KIL events and, if any exist, calls workflow_dispatch on sync-kil.yml
// to absorb-feedback + republish the KIL .docx. While the workflow
// runs, the SPA polls fetchSyncKilStatus and shows a subtle header
// indicator. This module is the thin client wrapper plus the UI hook
// that manages the indicator + polling lifecycle.

import { ApiError, getJson, postJson } from "./api";

export interface SyncKilStatus {
  in_flight: boolean;
  dispatched_at?: string;
  pending_event_count?: number;
}

export interface SyncKilDispatchResponse {
  dispatched: boolean;
  reason?: string;
  status?: { dispatched_at: string; pending_event_count: number };
}

export async function triggerSyncKil(): Promise<SyncKilDispatchResponse> {
  try {
    return await postJson<SyncKilDispatchResponse>("/sync-kil", {});
  } catch (err) {
    if (err instanceof ApiError) {
      // 503 means the instance hasn't wired up GITHUB_DISPATCH_TOKEN /
      // SYNC_KIL_WORKFLOW_REPO. Treat as soft no-op so the SPA degrades
      // gracefully — Generate will still work against whatever stale
      // kil-doc the last cron published.
      if (err.status === 503) {
        return { dispatched: false, reason: "dispatch-not-configured" };
      }
      // 502 / 400 / 401 etc. — log + return a non-dispatched marker so
      // callers don't crash. The header just won't appear.
      console.warn("[sync-kil] trigger failed:", err.status, err.body);
      return { dispatched: false, reason: `error-${err.status}` };
    }
    console.warn("[sync-kil] trigger threw:", err);
    return { dispatched: false, reason: "network-error" };
  }
}

export async function fetchSyncKilStatus(): Promise<SyncKilStatus> {
  return getJson<SyncKilStatus>("/sync-kil-status");
}

// Render the in-flight status header text. Caller controls visibility
// via `mount`/`unmount`. Pure DOM, no framework — matches the rest of
// the SPA. Returns a tuple of the wrapper + an updater so the caller
// can flip text without re-rendering.
export interface SyncKilStatusHeader {
  el: HTMLElement;
  show: (status: SyncKilStatus) => void;
  hide: () => void;
}

export function createSyncKilStatusHeader(): SyncKilStatusHeader {
  const el = document.createElement("div");
  el.className = "sync-kil-status";
  el.style.display = "none";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");

  const spinner = document.createElement("span");
  spinner.className = "sync-kil-spinner";
  spinner.setAttribute("aria-hidden", "true");
  el.appendChild(spinner);

  const text = document.createElement("span");
  text.className = "sync-kil-text";
  el.appendChild(text);

  return {
    el,
    show: (status: SyncKilStatus) => {
      const n = status.pending_event_count ?? 0;
      const mark = n === 1 ? "1 mark" : `${n} marks`;
      text.textContent = n > 0
        ? `Refreshing your KIL log… (${mark} pending)`
        : "Refreshing your KIL log…";
      el.style.display = "";
    },
    hide: () => {
      el.style.display = "none";
    },
  };
}

// Module-level reference to the active poller so a re-entry into the
// KIL view (refresh, back-forward, etc.) cleans up the previous loop
// before starting a new one. Tests can also call stopActiveSyncKilPoll
// directly to assert no leak.
let activePoller: ReturnType<typeof setInterval> | null = null;

export function stopActiveSyncKilPoll(): void {
  if (activePoller !== null) {
    clearInterval(activePoller);
    activePoller = null;
  }
}

export interface StartPollOptions {
  intervalMs?: number;
  onStatus: (status: SyncKilStatus) => void;
  onError?: (err: unknown) => void;
}

// Start polling /sync-kil-status. Stops automatically when status
// reports in_flight=false. Returns a stop() the caller can invoke
// on navigation away.
export function startSyncKilPoll(opts: StartPollOptions): () => void {
  stopActiveSyncKilPoll();
  const intervalMs = opts.intervalMs ?? 5000;
  const tick = async (): Promise<void> => {
    try {
      const status = await fetchSyncKilStatus();
      opts.onStatus(status);
      if (!status.in_flight) {
        stopActiveSyncKilPoll();
      }
    } catch (err) {
      // On poll failure, stop — don't spam a flaky endpoint. The
      // header will just sit at its last state until the next view
      // entry triggers a fresh poll.
      opts.onError?.(err);
      stopActiveSyncKilPoll();
    }
  };
  activePoller = setInterval(tick, intervalMs);
  return stopActiveSyncKilPoll;
}
